/**
 * AVBP Leads "Lenses" — PURE view-only derivations (AD-046, Phase 0 + A).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE SHARED CONTRACT (Phase 0, step 1)
 * ─────────────────────────────────────────────────────────────────────────
 *  This module owns the ONE thing both halves of the build depend on: how a
 *  lead's SOURCE is named. The live `LeadSource` enum has 4 values
 *  (website · free_tool · manual_referral · whatsapp); the owner's target
 *  reading is 5 (Referral · Facebook · Google · Website · Fiverr). Per the
 *  locked decision (L-3, option b) we DO NOT change the live enum — we fold
 *  it to the target labels for DISPLAY ONLY here. The Existing view keeps
 *  showing the live labels; every new Lens view reads `sourceMap()`. L0
 *  leads (AD-047) lean on this same map, so their sub-tag sits cleanly under
 *  the 5-source reading.
 *
 *  Everything here is PURE (no React, no Firebase) and unit-tested directly
 *  by the Node runner. The views are dumb; the logic is testable.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  ALL_LEAD_STATES,
  LEAD_SPINE,
  LEAD_STATE_LABEL,
  leadAgeDays,
  type Lead,
  type LeadSource,
  type LeadState,
} from './leads';
import { ALL_FSTAGES, FSTAGE_LABEL, fstageOf, type FStage } from './leadPipeline';

/* ════════════════════ Source fold (the 5-source lens reading) ════════════════════ */

/** Owner's target source set (display-only — never a stored enum value). */
export type TargetSource =
  | 'Website'
  | 'Referral'
  | 'Facebook'
  | 'Google'
  | 'Fiverr';

/** Display order of the 5 source chips. */
export const TARGET_SOURCES: TargetSource[] = [
  'Website',
  'Referral',
  'Facebook',
  'Google',
  'Fiverr',
];

/**
 * Fold any stored source (the live 4-value enum, plus the future additive
 * `facebook`/`google`/`fiverr` values) onto the owner's 5-source reading.
 * DISPLAY ONLY — no enum change, no write. Locked L-3 defaults:
 *   website          → Website
 *   free_tool        → Website   (lead-magnet results read as Website)
 *   manual_referral  → Referral
 *   whatsapp         → Referral  (inbound WA folds into Referral)
 *   facebook         → Facebook  (future additive value)
 *   google           → Google    (future additive value)
 *   fiverr           → Fiverr    (future additive value — its own pipeline)
 */
export function sourceMap(source: LeadSource | string): TargetSource {
  switch (String(source)) {
    case 'website':
    case 'free_tool':
      return 'Website';
    case 'manual_referral':
    case 'whatsapp':
      return 'Referral';
    case 'facebook':
      return 'Facebook';
    case 'google':
      return 'Google';
    case 'fiverr':
      return 'Fiverr';
    default:
      return 'Website';
  }
}

/**
 * A stable colour per target source (card tag / map pin / calendar chip).
 * Reads off the live token palette feel (accent / info / ai / warn / terra)
 * without importing CSS — components apply these as inline accents.
 */
export const SOURCE_COLOUR: Record<TargetSource, string> = {
  Website: '#008069', // accent (green)
  Referral: '#9E5E3B', // terra
  Facebook: '#2563EB', // info (blue)
  Google: '#B45309', // warn (amber)
  Fiverr: '#6D28D9', // ai (violet)
};

export function sourceColour(source: LeadSource | string): string {
  return SOURCE_COLOUR[sourceMap(source)];
}

/* ════════════════════ Grouping (List / Board / filters) ════════════════════ */

/** Group leads by their live pipeline `state`. Every state key is present. */
export function groupByState(leads: Lead[]): Record<LeadState, Lead[]> {
  const out = {} as Record<LeadState, Lead[]>;
  for (const s of ALL_LEAD_STATES) out[s] = [];
  for (const l of leads) {
    (out[l.state] ?? (out[l.state] = [])).push(l);
  }
  return out;
}

/** Group leads by their folded 5-source label. Every target key is present. */
export function groupBySource(leads: Lead[]): Record<TargetSource, Lead[]> {
  const out = {} as Record<TargetSource, Lead[]>;
  for (const s of TARGET_SOURCES) out[s] = [];
  for (const l of leads) {
    const t = sourceMap(l.source);
    (out[t] ?? (out[t] = [])).push(l);
  }
  return out;
}

/* ════════════════════ Board columns (Standard vs Fiverr) ════════════════════ */

export interface BoardColumn {
  /** state | fstage value used as the drop key. */
  key: string;
  label: string;
  /** off-ramp columns (parked/rejected) collapse into a side rail. */
  offRamp?: boolean;
  /** terminal column (converted / review) — no further forward drop. */
  terminal?: boolean;
}

/**
 * The columns for a board, given the pipeline. Standard = the live spine
 * order (off-ramps parked/rejected flagged for the collapsed "Lost/Parked"
 * rail, converted terminal). Fiverr = the `fstage` order, review terminal.
 * Locked L-8: parked/rejected in a rail, converted terminal.
 */
export function boardColumnsFor(pipeline: 'standard' | 'fiverr'): BoardColumn[] {
  if (pipeline === 'fiverr') {
    return ALL_FSTAGES.map((s) => ({
      key: s,
      label: FSTAGE_LABEL[s],
      terminal: s === 'review',
    }));
  }
  // Standard — the forward spine as columns; off-ramps as rail.
  const cols: BoardColumn[] = LEAD_SPINE.map((s) => ({
    key: s,
    label: LEAD_STATE_LABEL[s],
    terminal: s === 'converted',
  }));
  cols.push({ key: 'parked', label: LEAD_STATE_LABEL.parked, offRamp: true });
  cols.push({ key: 'rejected', label: LEAD_STATE_LABEL.rejected, offRamp: true });
  return cols;
}

/** The forward board columns only (no off-ramp rail) — for the column grid. */
export function boardForwardColumns(
  pipeline: 'standard' | 'fiverr',
): BoardColumn[] {
  return boardColumnsFor(pipeline).filter((c) => !c.offRamp);
}

/** The off-ramp columns (parked/rejected) — the collapsed rail. */
export function boardOffRampColumns(
  pipeline: 'standard' | 'fiverr',
): BoardColumn[] {
  return boardColumnsFor(pipeline).filter((c) => c.offRamp);
}

/* ════════════════════ Calendar bucket ════════════════════ */

/**
 * Resolve the date a lead sits on in the Calendar (locked L-6: bucket on
 * existing dated seams for v1, no new `nextActionAt` field). Priority:
 *   consultBookedAt → discoveryDoneAt → updatedAt → null (Undated tray).
 * Returns a YYYY-MM-DD key (local-agnostic, sliced off the ISO) or null.
 */
export function calendarBucket(lead: Lead): string | null {
  const iso =
    lead.consultBookedAt || lead.discoveryDoneAt || lead.updatedAt || '';
  if (!iso) return null;
  // Slice the date part off any ISO-ish string; guard a bad value.
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : null;
}

/** Group leads by their calendar bucket date; undated leads under ''. */
export function groupByCalendarDate(leads: Lead[]): Record<string, Lead[]> {
  const out: Record<string, Lead[]> = {};
  for (const l of leads) {
    const k = calendarBucket(l) ?? '';
    (out[k] ?? (out[k] = [])).push(l);
  }
  return out;
}

/* ════════════════════ Map clustering (schematic v1) ════════════════════ */

/**
 * Schematic map cluster key — the existing free-text `location` string,
 * normalised (trimmed, lowercased, collapsed whitespace). Locked L-2:
 * schematic for the pilot, zero new geo data; live Google Maps is a
 * fast-follow that adds city/coords.
 */
export function locationCluster(lead: Lead): string {
  const s = (lead.location || '').trim().replace(/\s+/g, ' ');
  return s ? s.toLowerCase() : '';
}

export interface MapCluster {
  /** normalised cluster key ('' = Unlocated). */
  key: string;
  /** a human label (the first non-empty original location string). */
  label: string;
  leads: Lead[];
}

/** Cluster leads by their `location` string; '' key = "Unlocated" tray. */
export function clusterByLocation(leads: Lead[]): MapCluster[] {
  const map = new Map<string, MapCluster>();
  for (const l of leads) {
    const key = locationCluster(l);
    let c = map.get(key);
    if (!c) {
      c = { key, label: key ? l.location.trim() : 'Unlocated', leads: [] };
      map.set(key, c);
    }
    c.leads.push(l);
  }
  return [...map.values()].sort((a, b) => b.leads.length - a.leads.length);
}

/* ════════════════════ Search + sort (List view) ════════════════════ */

/**
 * Client-side lead search (locked L-7: add a search box in List). Matches
 * the query (case-insensitive) against name / id / phone / email / location
 * / concern. Empty query → matches everything.
 */
export function leadMatchesSearch(lead: Lead, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    lead.name,
    lead.id,
    lead.phone,
    lead.email,
    lead.location,
    lead.concern,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export type LeadSortKey = 'age' | 'value' | 'name';

/** The agreed/standard value of a lead in its own units (0 when none). */
export function leadValue(lead: Lead): number {
  if (typeof lead.agreedValue === 'number' && lead.agreedValue > 0) {
    return lead.agreedValue;
  }
  return lead.quote?.valueINR ?? 0;
}

/**
 * Pure sort (returns a NEW array). `age` = newest first (largest age last?).
 * We sort age ascending (oldest leads — the ones to chase — on top) when
 * `dir==='desc'` is false; callers pass an explicit direction. Default: age
 * descending = oldest first is handled by the caller toggling `dir`.
 */
export function sortLeads(
  leads: Lead[],
  key: LeadSortKey,
  dir: 'asc' | 'desc',
  nowISO: string,
): Lead[] {
  const sign = dir === 'asc' ? 1 : -1;
  const copy = [...leads];
  copy.sort((a, b) => {
    let cmp = 0;
    if (key === 'age') {
      cmp = leadAgeDays(a, nowISO) - leadAgeDays(b, nowISO);
    } else if (key === 'value') {
      cmp = leadValue(a) - leadValue(b);
    } else {
      cmp = a.name.localeCompare(b.name);
    }
    return cmp * sign;
  });
  return copy;
}

/* ════════════════════ Stage progress (List "N of M") ════════════════════ */

/**
 * The lead's position on the forward spine as { n, m } for the List
 * "Current stage (N of M)" mini-progress. Off-ramp states report their last
 * spine position is unknown → n=0. Converted → n=m (complete).
 */
export function spineProgress(state: LeadState): { n: number; m: number } {
  const m = LEAD_SPINE.length;
  const idx = LEAD_SPINE.indexOf(state);
  if (idx < 0) return { n: 0, m }; // off-ramp (parked/rejected)
  return { n: idx + 1, m };
}

/** Fiverr progress for the List mini-bar when a lead is on the Fiverr pipeline. */
export function fstageProgress(lead: Lead): { n: number; m: number; label: string } {
  const s: FStage = fstageOf(lead);
  const m = ALL_FSTAGES.length;
  const idx = ALL_FSTAGES.indexOf(s);
  return { n: idx + 1, m, label: FSTAGE_LABEL[s] };
}
